import { useEffect, useState } from 'react';
import { Star, Send, Loader2, Sparkles, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable feedback form. Used as a standalone /feedback page AND embedded
 * inside the vendor + consumer dashboard "Feedback" tabs.
 *
 * Props:
 *   source?: 'survey' | 'ongoing'  (defaults to 'ongoing' inside dashboards)
 *   onSubmitted?: () => void
 */
export default function FeedbackForm({ source = 'ongoing', onSubmitted }) {
  const { getAuthHeaders, user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetch(`${API}/feedback/questions`)
      .then((r) => r.json())
      .then((d) => setQuestions(d.questions || []))
      .catch(() => toast.error('Failed to load survey'))
      .finally(() => setLoading(false));
  }, []);

  const setAnswer = (id, value) => setAnswers((p) => ({ ...p, [id]: value }));

  const toggleCheck = (id, option) => {
    setAnswers((p) => {
      const cur = Array.isArray(p[id]) ? [...p[id]] : [];
      const i = cur.indexOf(option);
      if (i >= 0) cur.splice(i, 1); else cur.push(option);
      return { ...p, [id]: cur };
    });
  };

  const submit = async () => {
    if (!user) {
      toast.error('Please sign in first to submit feedback');
      return;
    }
    const hasAny = Object.values(answers).some((v) => v && (typeof v !== 'object' ? true : (Array.isArray(v) ? v.length > 0 : Object.keys(v).length > 0)));
    if (!hasAny) {
      toast.error('Answer at least one question');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/feedback/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ answers, source }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Submit failed');
      toast.success('Thanks — we got your feedback');
      setSubmitted(true);
      onSubmitted?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-[#9CA3AF]" data-testid="feedback-loading">Loading questions…</div>;
  }
  if (submitted) {
    return (
      <div className="text-center py-16 max-w-md mx-auto" data-testid="feedback-success">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-4">
          <Check className="text-emerald-400" size={28} />
        </div>
        <h2 className="text-white font-syne text-2xl font-bold">Thank you</h2>
        <p className="text-[#C4B5FD] mt-2 text-sm">Your feedback went straight to the founder. Drop more ideas any time on this same tab.</p>
        <button
          onClick={() => { setAnswers({}); setSubmitted(false); }}
          className="mt-5 px-5 py-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white text-sm"
          data-testid="feedback-new-submission"
        >
          Send another
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="feedback-form">
      <div className="bg-gradient-to-br from-[#8B5CF6]/15 to-[#3B82F6]/10 border border-[#8B5CF6]/30 rounded-2xl p-5">
        <div className="flex items-center gap-2 text-[#A78BFA] text-xs uppercase tracking-widest font-bold mb-1">
          <Sparkles size={14} /> Your voice shapes this platform
        </div>
        <p className="text-white text-sm leading-relaxed">
          Mix of star ratings, quick checklists, and open-ended boxes. Skip anything that doesn't apply — even one answer is gold.
        </p>
      </div>

      {questions.map((q, idx) => (
        <div key={q.id} className="bg-[#1E1E3A]/60 border border-white/10 rounded-2xl p-5" data-testid={`feedback-q-${q.id}`}>
          <p className="text-white font-medium text-sm mb-3">
            <span className="text-[#64748B] font-mono mr-2">{String(idx + 1).padStart(2, '0')}</span>
            {q.label}
          </p>

          {q.type === 'rating' && (
            <div className="flex gap-2" data-testid={`feedback-rating-${q.id}`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => setAnswer(q.id, n)}
                  className={`p-2.5 rounded-xl transition-all ${
                    answers[q.id] >= n
                      ? 'bg-amber-500/20 text-amber-300'
                      : 'bg-white/5 text-[#9CA3AF] hover:bg-white/10'
                  }`}
                  type="button"
                  data-testid={`feedback-${q.id}-${n}`}
                  aria-label={`${n} of 5`}
                >
                  <Star size={20} fill={answers[q.id] >= n ? 'currentColor' : 'none'} />
                </button>
              ))}
              {answers[q.id] && <span className="self-center ml-2 text-[#A78BFA] text-xs font-mono">{answers[q.id]}/5</span>}
            </div>
          )}

          {q.type === 'checklist' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`feedback-checklist-${q.id}`}>
              {q.options?.map((opt) => {
                const selected = (answers[q.id] || []).includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggleCheck(q.id, opt)}
                    className={`text-left text-sm px-3 py-2 rounded-xl border transition-all ${
                      selected
                        ? 'bg-[#8B5CF6]/20 border-[#8B5CF6]/50 text-white'
                        : 'bg-white/5 border-white/10 text-[#C4B5FD] hover:border-white/30'
                    }`}
                    data-testid={`feedback-${q.id}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {selected && <Check size={12} className="inline mr-1.5 text-[#A78BFA]" />}
                    {opt}
                  </button>
                );
              })}
            </div>
          )}

          {q.type === 'text' && (
            <input
              type="text"
              value={answers[q.id] || ''}
              onChange={(e) => setAnswer(q.id, e.target.value)}
              placeholder={q.placeholder || 'Type your answer…'}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-[#8B5CF6]"
              maxLength={300}
              data-testid={`feedback-text-${q.id}`}
            />
          )}

          {q.type === 'longtext' && (
            <textarea
              value={answers[q.id] || ''}
              onChange={(e) => setAnswer(q.id, e.target.value)}
              placeholder="Tell us what's on your mind… we read every single one."
              rows={4}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-[#8B5CF6]"
              maxLength={1500}
              data-testid={`feedback-longtext-${q.id}`}
            />
          )}
        </div>
      ))}

      <button
        onClick={submit}
        disabled={submitting}
        className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all flex items-center justify-center gap-2"
        data-testid="feedback-submit"
      >
        {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send feedback</>}
      </button>
    </div>
  );
}
